"use client"

import { Images } from "@/constants/images"
import { DefaultImage } from "@/lib/external-components"
import { toImageSrc } from "@/lib/utils"
import type { QuickServiceItemProps } from "@/types/types"
import Image from "next/image"

export const QuickServiceItem = ({
  item,
  className = "",
  isLarge = false,
  enableHover = false,
  index,
}: QuickServiceItemProps) => {
  const imageUrl = item.image ? toImageSrc(item.image) : DefaultImage
  const iconUrl = item.icon ? toImageSrc(item.icon) : DefaultImage
  const arrowIconUrl = Images.arrowIcon

  const containerHeight = isLarge ? "sm:h-full" : "sm:h-[450px]"

  // Kiểm tra xem có icon hay không để quyết định hiển thị
  const hasIcon = !!item.icon

  return (
    <div className={`w-full mx-auto sm:max-w-full sm:mx-0 ${className}`}>
      <div
        className={`relative w-full h-auto ${containerHeight} bg-gray-200 overflow-hidden rounded-sm cursor-pointer ${enableHover ? "group" : ""
          }`}
      >
        {/* Mobile: Image với height tự động */}
        <div className="relative w-full h-auto sm:hidden">
          <Image
            src={imageUrl}
            alt={item.text || item.title || "Quick service"}
            width={343}
            height={500}
            className="w-full h-auto object-cover"
            sizes="343px"
            style={{ height: "auto" }}
          />
        </div>
        {/* Desktop: Image với fill để giữ layout */}
        <div className="hidden sm:block absolute inset-0">
          <Image
            src={imageUrl}
            alt={item.text || item.title || "Quick service"}
            fill
            className="w-full h-full object-cover"
            sizes="(max-width: 1024px) 50vw, 41.35vw"
          />
        </div>

        {/* Gradient overlay: tối ở dưới, sáng dần lên trên - cho cả desktop và mobile */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent pointer-events-none z-10" />

        {/* Overlay với icon, title, description và arrow */}
        {enableHover && (
          <>
            {/* Mobile: Layout hình vuông với icon ở trên, title, description và arrow */}
            <div className="absolute inset-0 flex flex-col items-center justify-center px-4 py-8 bg-[#19B04E] text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:hidden z-20">
              {/* Chỉ render icon khi có icon */}
              {hasIcon && (
                <div className="relative w-[90px] h-[55.5px] sm:mb-6 mb-0">
                  <Image src={iconUrl} alt="Icon" fill className="object-contain" />
                </div>
              )}
              {item.title && (
                <h3
                  className={`${hasIcon ? "mt-4" : ""
                    } mb-[0.625rem] sm:mb-6 text-lg sm:text-xl font-bold leading-tight text-center uppercase`}
                >
                  {item.title}
                </h3>
              )}
              {item.description && (
                <p className="mb-6 max-w-[279px] sm:max-w-[395px] text-[#FFFFFFB3] text-sm leading-[1.3125rem] sm:leading-6 text-center">
                  {item.description}
                </p>
              )}
              {/* Arrow right */}
              <div className="relative block mx-auto w-[37px] h-[15px] mt-auto">
                <Image src={arrowIconUrl} alt="Arrow icon" fill className="object-contain" />
              </div>
            </div>

            {/* Desktop: Layout banner với icon, title, description và arrow */}
            <div className="hidden sm:flex absolute inset-0 flex-col items-center justify-center py-[4.75rem] bg-[#19B04E] text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100 z-20">
              {/* Chỉ render icon khi có icon */}
              {hasIcon && (
                <div className="relative w-[123px] h-[76px]">
                  <Image src={iconUrl} alt="Icon" fill className="w-12 h-12 mb-4 object-contain" />
                </div>
              )}
              {item.title && (
                <h3 className={`${hasIcon ? "mt-6" : ""} mb-8 text-xl font-bold leading-tight text-center uppercase`}>
                  {item.title}
                </h3>
              )}
              {item.description && (
                <p className="mb-6 sm:mb-0 max-w-[395px] text-[#FFFFFFB3] sm:text-base text-sm leading-6 text-center">
                  {item.description}
                </p>
              )}
              {/* Arrow right */}
              <div className="relative block mx-auto w-[37px] h-[15px] sm:mt-6">
                <Image src={arrowIconUrl} alt="Arrow icon" fill className="object-contain" />
              </div>
            </div>
          </>
        )}

        {/* Text hiển thị bên trong ảnh (dưới cùng) khi không dùng overlay hover */}
        {!enableHover && item.text && (
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center px-5 py-3 z-20">
            <p className="text-center text-sm font-semibold leading-6 text-white">{item.text}</p>
          </div>
        )}

        {/* Text hiển thị khi không hover - sẽ ẩn đi khi hover */}
        {enableHover && item.text && (
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-center px-5 py-3 z-20 transition-opacity duration-300 group-hover:opacity-0">
            <p className="text-center pb-5 text-xl font-normal leading-6 sm:leading-[1.9375rem] text-[#FFFFFF]">
              {item.text}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
